<template>
  <div
    :class="$style.root"
    :style="`--pagination-limit: ${Math.ceil(paginationLimit / 2)}`"
  >
    <div
      v-if="label"
      :class="$style.label"
    >
      {{ label?.trim() }}
    </div>
    <div
      :class="items?.length > limit ? $style.listContainer : null"
      :data-paginated="paginated && shouldPaginate ? '' : null"
    >
      <ul
        v-if="items?.length"
        :class="[$style.list, items?.length > limit ? $style.listExtended : $style.listDefault]"
      >
        <li
          :class="$style.item"
          v-for="(item, index) in items"
          :key="index"
        >
          <i18nLink
            v-if="item?.richText"
            :class="[$style.link, $style.itemLabel]"
            :to="routeObject(item?.id)"
          >
            {{ item?.label?.trim() }}
          </i18nLink>
          <span
            v-else
            :class="$style.itemLabel"
            v-html="item?.label?.trim()"
          />
        </li>
      </ul>
    </div>

    <button
      v-if="paginated && shouldPaginate"
      :class="$style.button"
      @click="paginated = false"
    >
      {{ $t('shared.showAll', {count: items?.length }) }}
    </button>

    <Teleport to="body">
      <CoreBlocksListWithPopupModalComponent
        :item="item"
        :closePath="closePath"
        v-if="item"
      />
    </Teleport>
  </div>
</template>

<script lang="ts" setup>
  const props = defineProps({
    label: {
      type: String,
      required: false,
    },
    items: {
      type: Array,
      required: true
    },
    blockId: {
      type: [String, Number]
    },
  })

  const items = computed(() => {
    return props.items?.filter(item => item?.label)
  })

  const limit = 4
  const paginationLimit = 18
  const paginated = ref(true)
  const shouldPaginate = computed(() => {
    return props.items?.length > paginationLimit
  })

  const nuxtApp = useNuxtApp()
  const routeName = computed(() => nuxtApp.$getRouteBaseName())
  const route = useRoute()

  const routeObject = (id) => {
    return {
      name: routeName?.value,
      params: {
        slug: route?.params?.slug
      },
      query: {
        'block': props.blockId,
        'item': id,
      }
    }
  }

  const closePath = nuxtApp.$localePath({
    name: routeName?.value,
    params: {
      slug: route?.params?.slug
    }
  })

  const item = computed(() => {
    const blockQuery = route?.query?.block
    const itemQuery = route?.query?.item
    return props.items?.find(item => {
      return String(item?.id) === itemQuery && String(props?.blockId) === blockQuery
    })
  })
</script>

<style module>
.root {
  --block--margin-bottom: calc(var(--unit--vertical) * 2);
}

[class*='components-core-blocks-rich-text'] + .root {
  --block--margin-top: calc(var(--unit--vertical) * 2);
}

.label {
  composes: font-helvetica font-size-small from global;
  composes: label from global;
}

.list {
  composes: reset-list from global;
}

.listDefault {
  composes: font-size-default from global;
}

.listExtended {
  composes: font-size-default from global;
  columns: 2;
  gap: var(--unit--default);

  position: relative;
}

.listContainer[data-paginated] {
  max-height: calc(var(--lh,) * var(--pagination-limit, 1));
  overflow: hidden;
}

.listExtended .itemLabel {
  display: inline-block;
  break-inside: avoid;
  text-indent: -2ch;
  padding-left: 2ch;
}

.listDefault .item {
  display: inline-block;
}

.listDefault .item:not(:last-of-type):after {
  content: ', ';
  white-space: pre-wrap;
}

.button {
  composes: reset-button font-helvetica font-size-small link from global;
  margin-top: var(--padding--element--top);
}

.link {
  composes: reset-link link from global;
}

.indicator {
  composes: font-size-small font-helvetica from global;
}

.heading {
  composes: reset-heading font-size-large from global;
}

.richText {
  margin-top: var(--unit--default);
}
</style>
